import { useState } from "react";

interface Project {
  id: string;
  title: string;
  status: string;
  progress: number;
  budget?: number;
  spent?: number;
}

interface ProjectCompletionMetricsProps {
  projects: Project[];
}

function RadialProgress({ value, color, size = 64 }: { value: number; color: string; size?: number }) {
  const radius = (size - 8) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (value / 100) * circumference;

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#1a1a2e"
          strokeWidth={6}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={color}
          strokeWidth={6}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          style={{ transition: "stroke-dashoffset 0.6s ease" }}
        />
      </svg>
      <span
        className="absolute text-xs font-black"
        style={{ color, fontFamily: "'Space Grotesk', sans-serif" }}
      >
        {value}%
      </span>
    </div>
  );
}

export default function ProjectCompletionMetrics({ projects }: ProjectCompletionMetricsProps) {
  const [selectedProject, setSelectedProject] = useState(projects[0]?.id || "");
  const project = projects.find((p) => p.id === selectedProject) || projects[0];

  const budgetUsedPct = project?.budget && project.spent ? Math.round((project.spent / project.budget) * 100) : 0;
  const avgProgress = Math.round(projects.reduce((sum, p) => sum + p.progress, 0) / projects.length) || 0;

  const statusColors: Record<string, string> = {
    active: "text-cyber-green bg-cyber-green/10 border-cyber-green/30",
    completed: "text-electric-blue bg-electric-blue/10 border-electric-blue/30",
    paused: "text-cyber-orange bg-cyber-orange/10 border-cyber-orange/30",
  };

  const projectColor = "#00D4FF"; // Default color

  return (
    <div className="bg-dark-card border border-dark-border rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-dark-border">
        <div>
          <h2 className="text-white font-bold text-sm" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Project Completion Metrics
          </h2>
          <p className="text-silver-dim text-xs mt-0.5">Average progress across all projects</p>
        </div>
      </div>

      {/* Overall Progress */}
      <div className="p-5 text-center">
        <RadialProgress value={avgProgress} color={projectColor} size={120} />
        <p className="text-white font-bold text-sm mt-3">{avgProgress}%</p>
        <p className="text-silver-dim text-xs">Average Progress</p>
        <p className="text-silver text-xs mt-1">({projects.length} active projects)</p>
      </div>

      {/* Project List */}
      <div className="px-5 pb-4">
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {projects.slice(0, 5).map((p) => (
            <button
              key={p.id}
              onClick={() => setSelectedProject(p.id)}
              className={`w-full flex items-center gap-3 p-3 rounded-lg transition-all cursor-pointer text-left hover:bg-dark-panel ${
                selectedProject === p.id ? "bg-dark-panel border border-dark-border ring-1 ring-electric-blue/20" : ""
              }`}
            >
              <div className="w-2 h-2 rounded-full" style={{ background: projectColor }} />
              <span className="text-white text-xs font-medium flex-1 truncate">{p.title}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full border font-medium ${statusColors[p.status || 'active'] || 'border-dark-border text-silver-dim'}`}>
                {p.status || 'active'}
              </span>
              <div className="flex items-center gap-1 flex-shrink-0">
                <div className="w-12 bg-dark-border rounded-full h-1.5">
                  <div className="h-1.5 rounded-full bg-electric-blue" style={{ width: `${p.progress}%` }} />
                </div>
                <span className="text-xs font-bold text-electric-blue">{p.progress}%</span>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
